import React from 'react'

/** Einheitliche Farblogik: ok = grün, warn = gelb (Wartung/geplant), bad = rot (defekt/Problem),
 *  neutral = grau (außer Dienst/beendet), info = blau (laufend/im Einsatz). */
export type StatusTon = 'ok' | 'warn' | 'bad' | 'neutral' | 'info'

const STATUS: Record<string, { label: string; ton: StatusTon }> = {
  VERFUEGBAR: { label: 'Verfügbar', ton: 'ok' },
  EINSATZBEREIT: { label: 'Einsatzbereit', ton: 'ok' },
  AKTIV: { label: 'Aktiv', ton: 'ok' },
  GUELTIG: { label: 'Gültig', ton: 'ok' },
  IM_EINSATZ: { label: 'Im Einsatz', ton: 'info' },
  LADEN: { label: 'Lädt', ton: 'info' },
  LAUFEND: { label: 'Laufend', ton: 'info' },
  ABFAHRT_WACHE: { label: 'Abfahrt Wache', ton: 'info' },
  ANKUNFT_EINSATZORT: { label: 'Am Einsatzort', ton: 'info' },
  WARTUNG: { label: 'In Wartung', ton: 'warn' },
  GEPLANT: { label: 'Geplant', ton: 'warn' },
  VORBEREITUNG: { label: 'Vorbereitung', ton: 'warn' },
  ALARMIERUNG: { label: 'Alarmiert', ton: 'warn' },
  DEFEKT: { label: 'Defekt', ton: 'bad' },
  ABGEBROCHEN: { label: 'Abgebrochen', ton: 'bad' },
  ABGELAUFEN: { label: 'Abgelaufen', ton: 'bad' },
  AUSSER_BETRIEB: { label: 'Außer Dienst', ton: 'neutral' },
  AUSSER_DIENST: { label: 'Außer Dienst', ton: 'neutral' },
  INAKTIV: { label: 'Inaktiv', ton: 'neutral' },
  BEENDET: { label: 'Beendet', ton: 'neutral' },
  EINSATZENDE: { label: 'Einsatzende', ton: 'neutral' },
  ABGESCHLOSSEN: { label: 'Abgeschlossen', ton: 'neutral' },
  // Prioritäten
  KRITISCH: { label: 'Kritisch', ton: 'bad' },
  HOCH: { label: 'Hoch', ton: 'warn' },
  NORMAL: { label: 'Normal', ton: 'neutral' },
  NIEDRIG: { label: 'Niedrig', ton: 'neutral' },
}

/** Deutsches Label zu einem Rohwert (z. B. 'AUSSER_BETRIEB' -> 'Außer Dienst'); Fallback: lesbar formatiert. */
export function statusLabel(wert: string | null | undefined): string {
  if (!wert) return '—'
  const k = wert.toUpperCase()
  if (STATUS[k]) return STATUS[k].label
  const t = wert.replace(/_/g, ' ').toLowerCase()
  return t.charAt(0).toUpperCase() + t.slice(1)
}

export function statusTon(wert: string | null | undefined): StatusTon {
  return STATUS[(wert || '').toUpperCase()]?.ton ?? 'neutral'
}

const PUNKT: Record<StatusTon, string> = {
  ok: 'bg-emerald-400', warn: 'bg-amber-400', bad: 'bg-red-400', neutral: 'bg-slate-400', info: 'bg-sky-400',
}
export const STATUS_PUNKT = PUNKT
const TEXT: Record<StatusTon, string> = {
  ok: 'text-emerald-200', warn: 'text-amber-200', bad: 'text-red-200', neutral: 'text-dark-text-muted', info: 'text-sky-200',
}

/** Status als Punkt + Text (nie nur Farbe). `wert` = Rohwert; `label`/`ton` überschreiben die Zuordnung. */
export function StatusBadge({ wert, label, ton }: { wert?: string | null; label?: string; ton?: StatusTon }) {
  const t = ton ?? statusTon(wert)
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium ${TEXT[t]}`}>
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${PUNKT[t]}`} />
      {label ?? statusLabel(wert)}
    </span>
  )
}

/** Kompakte Chips (max. `max` sichtbar, Rest als „+N“ mit Tooltip). */
export function ChipListe({ items, max = 3, klasse = 'border-sky-500/30 bg-sky-500/10 text-sky-200' }: {
  items: string[]; max?: number; klasse?: string
}) {
  if (items.length === 0) return <span className="text-xs text-dark-text-subtle">—</span>
  const sichtbar = items.slice(0, max)
  const rest = items.slice(max)
  const chip = 'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium'
  return (
    <span className="flex flex-wrap items-center gap-1">
      {sichtbar.map(i => <span key={i} className={`${chip} ${klasse}`}>{i}</span>)}
      {rest.length > 0 && <span title={rest.join(', ')} className={`${chip} border-dark-border bg-dark-bg-tertiary text-dark-text-muted`}>+{rest.length}</span>}
    </span>
  )
}
